import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

export default function AccountSettings() {
  const { user, profile, updateProfile, signOut } = useAuth();
  const [form, setForm] = useState({
    full_name: '',
    phone: '',
    notification_method: 'email',
    role: 'buyer'
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [pwForm, setPwForm] = useState({ password: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSaved, setPwSaved] = useState(false);
  const [pwError, setPwError] = useState('');

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
        notification_method: profile.notification_method || 'email',
        role: profile.role || 'buyer'
      });
    }
  }, [profile]);

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);

    const { error } = await updateProfile(form);
    if (error) {
      setError(error.message);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
    setSaving(false);
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwError('');
    setPwSaved(false);

    if (pwForm.password !== pwForm.confirm) {
      setPwError('Passwords do not match.');
      return;
    }
    if (pwForm.password.length < 8) {
      setPwError('Password must be at least 8 characters.');
      return;
    }

    setPwSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pwForm.password });
    if (error) {
      setPwError(error.message);
    } else {
      setPwSaved(true);
      setPwForm({ password: '', confirm: '' });
      setTimeout(() => setPwSaved(false), 3000);
    }
    setPwSaving(false);
  }

  if (!user) return (
    <div className="page" style={{ textAlign: 'center', padding: 60 }}>
      <p style={{ color: 'var(--text-secondary)' }}>Please sign in to view account settings.</p>
    </div>
  );

  return (
    <div className="page" style={{ maxWidth: 680 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 6 }}>account settings</h1>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 28 }}>{user.email}</p>

      {/* Profile */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 16 }}>profile information</h3>
        <form onSubmit={handleSaveProfile}>
          <div className="input-group">
            <label>full name</label>
            <input
              type="text"
              value={form.full_name}
              onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
              placeholder="Your full name"
            />
          </div>

          <div className="input-group">
            <label>phone number</label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="+1 (555) 000-0000"
            />
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
              Required for SMS notifications
            </div>
          </div>

          <div className="input-group">
            <label>I am a</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['buyer', 'Buyer'], ['owner', 'Owner'], ['both', 'Both']].map(([val, label]) => (
                <div
                  key={val}
                  onClick={() => setForm(f => ({ ...f, role: val }))}
                  style={{
                    flex: 1, padding: '10px 14px', border: `1px solid ${form.role === val ? 'var(--blue-mid)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)', background: form.role === val ? 'var(--blue-light)' : 'var(--bg)',
                    cursor: 'pointer', textAlign: 'center', fontSize: 13, fontWeight: 500,
                    color: form.role === val ? 'var(--blue-dark)' : 'var(--text)'
                  }}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>

          <div className="input-group">
            <label>default notification method</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['in-app', 'in-app'], ['email', 'email'], ['sms', 'SMS'], ['all', 'all']].map(([val, label]) => (
                <div
                  key={val}
                  onClick={() => setForm(f => ({ ...f, notification_method: val }))}
                  style={{
                    flex: 1, padding: '10px 8px', border: `1px solid ${form.notification_method === val ? 'var(--blue-mid)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)', background: form.notification_method === val ? 'var(--blue-light)' : 'var(--bg)',
                    cursor: 'pointer', textAlign: 'center', fontSize: 12,
                    color: form.notification_method === val ? 'var(--blue-dark)' : 'var(--text-secondary)'
                  }}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', color: '#991B1B', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}

          {saved && (
            <div style={{ background: 'var(--green-light)', border: '1px solid #C0DD97', color: 'var(--green)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
              Profile saved successfully!
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'saving...' : 'save profile'}
          </button>
        </form>
      </div>

      {/* Change password */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 16 }}>change password</h3>
        <form onSubmit={handleChangePassword}>
          <div className="input-group">
            <label>new password</label>
            <input
              type="password"
              value={pwForm.password}
              onChange={e => setPwForm(f => ({ ...f, password: e.target.value }))}
              placeholder="At least 8 characters"
              minLength={8}
            />
          </div>
          <div className="input-group">
            <label>confirm new password</label>
            <input
              type="password"
              value={pwForm.confirm}
              onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
              placeholder="Repeat new password"
            />
          </div>

          {pwError && (
            <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', color: '#991B1B', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
              {pwError}
            </div>
          )}
          {pwSaved && (
            <div style={{ background: 'var(--green-light)', border: '1px solid #C0DD97', color: 'var(--green)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
              Password changed successfully!
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={pwSaving}>
            {pwSaving ? 'saving...' : 'change password'}
          </button>
        </form>
      </div>

      {/* Danger zone */}
      <div className="card" style={{ border: '1px solid #FECACA' }}>
        <h3 style={{ marginBottom: 8, color: '#991B1B' }}>sign out</h3>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
          Sign out of your Homes4Bid account on this device.
        </p>
        <button className="btn" style={{ color: '#991B1B', borderColor: '#FECACA' }} onClick={signOut}>
          sign out
        </button>
      </div>
    </div>
  );
}
